import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../context/TrackerContext';
import { reviewCheck, reviewSpotCheck, taskGet } from '../lib/api';
import type { ReviewCheckData, ReviewCheckRow, StudentTaskRow } from '../lib/api';
import { TaskHistory } from './TaskReviews';

// ── Review Check ─────────────────────────────────────────────
// For Student Coordinators and the admin: are the mentors giving feedback
// properly? Each mentor's numbers in the range (reviews, on time within 48
// hours, average score, Redo, words, "helpful" taps), red flags worked out
// from them (short feedback, the same feedback copied to many students, the
// same score every time, never a Redo, late reviews), and every review to
// spot-check: 👍 good review, or 👎 needs better feedback with a note the
// mentor gets. Mirrors the app's review_check_screen.dart; server: routes/tasks.js.

const IST = 'Asia/Kolkata';
const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { timeZone: IST, day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });
const pct = (n: number | null) => (n === null ? '—' : `${n}%`);

type Filter = 'all' | 'flagged' | 'unchecked';

const SpotCheck: React.FC<{ auth: { adminPassword: string }; row: ReviewCheckRow; onClose: () => void; onDone: () => void }> = ({ auth, row, onClose, onDone }) => {
  const [task, setTask] = useState<StudentTaskRow | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { taskGet(auth, row.taskId).then(r => setTask(r.task)).catch((e: any) => toast.error(e.message)); }, [auth, row.taskId]);
  const mark = async (verdict: 'good' | 'needs') => {
    if (verdict === 'needs' && !note.trim()) { toast.error('Say what the feedback is missing (the mentor sees this)'); return; }
    setBusy(true);
    try {
      await reviewSpotCheck(auth.adminPassword, row.taskId, row.n, verdict, note.trim());
      toast.success(verdict === 'good' ? `Marked good. ${row.byName} gets a 👍.` : `${row.byName} gets your note.`);
      onDone();
      onClose();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-3" role="dialog" aria-modal="true" aria-label="Check the review">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[94vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 flex items-start justify-between gap-3" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
          <div className="min-w-0">
            <p className="text-white font-bold truncate">{row.byName}'s review · {row.studentName}</p>
            <p className="text-white/60 text-xs mt-0.5">{row.title} · try {row.n} · {when(row.at)}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="overflow-y-auto p-5 grid md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">The student's work</p>
            {task ? <TaskHistory auth={auth} task={task} /> : <p className="text-sm text-gray-400">Loading…</p>}
          </div>
          <div className="space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">The review</p>
            <div className={`rounded-xl p-3 ${row.result === 'Accepted' ? 'bg-green-50' : 'bg-amber-50'}`}>
              <p className="text-sm font-bold text-gray-800">{row.score}/10 · {row.result}{row.late ? ' · late' : ''}</p>
              <p className="text-sm text-gray-700 mt-1"><b>Good:</b> {row.good}</p>
              <p className="text-sm text-gray-700"><b>Improve:</b> {row.improve}</p>
              <p className="text-[11px] text-gray-500 mt-1">{row.words} words{row.short ? ' · short' : ''}{row.copied ? ' · same text given to other students' : ''}{row.helpful ? ` · student: ${row.helpful === 'yes' ? '👍 helpful' : '👎 not helpful'}` : ''}</p>
            </div>
            {row.checks.map((c, i) => <p key={i} className={`text-xs font-semibold ${c.verdict === 'good' ? 'text-green-700' : 'text-red-700'}`}>{c.verdict === 'good' ? '👍' : '👎'} {c.byName}{c.note ? `: ${c.note}` : ''}</p>)}
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={600} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-[#2b2240]"
              placeholder="A note for the mentor (needed for 👎), e.g. Say exactly which line is wrong and how to fix it" aria-label="Note for the mentor" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => mark('good')} disabled={busy} className="py-2.5 rounded-xl text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">👍 Good review</button>
              <button onClick={() => mark('needs')} disabled={busy} className="py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">👎 Needs better feedback</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReviewCheck: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const auth = useMemo(() => ({ adminPassword }), [adminPassword]);
  const [from, setFrom] = useState(`${today().slice(0, 8)}01`);
  const [to, setTo] = useState(today());
  const [data, setData] = useState<ReviewCheckData | null>(null);
  const [mentor, setMentor] = useState('');
  const [filter, setFilter] = useState<Filter>('flagged');
  const [open, setOpen] = useState<ReviewCheckRow | null>(null);

  const load = useCallback(async () => {
    try { setData(await reviewCheck(adminPassword, from, to)); } catch (e: any) { toast.error(e.message); }
  }, [adminPassword, from, to]);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.reviews || []).filter(r => (!mentor || r.byId === mentor)
    && (filter === 'all' || (filter === 'flagged' ? r.short || r.copied || r.late || r.helpful === 'no' : !r.checks.length)));

  return (
    <TrackerLayout>
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Review Check</h1>
            <p className="text-gray-500 text-sm mt-0.5">Are the mentors giving feedback properly? Open a review, see the student's work next to it, and mark it 👍 or 👎.</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} className="px-3 py-2 border-2 border-gray-200 rounded-xl text-sm" aria-label="From" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={to} min={from} max={today()} onChange={e => setTo(e.target.value)} className="px-3 py-2 border-2 border-gray-200 rounded-xl text-sm" aria-label="To" />
          </div>
        </div>

        {!data ? <p className="text-gray-400 text-sm py-10 text-center">Loading…</p> : (
          <>
            {data.lateNow > 0 && <p className="text-sm font-bold rounded-xl px-4 py-2.5 bg-red-50 text-red-700">⏰ {data.lateNow} review{data.lateNow === 1 ? ' is' : 's are'} late right now (over 48 hours)</p>}
            {!data.mentors.length ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500 text-sm">No reviews in these dates yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.mentors.map(m => (
                  <button key={m.id} onClick={() => setMentor(mentor === m.id ? '' : m.id)} aria-pressed={mentor === m.id}
                    className={`text-left bg-white rounded-2xl border-2 p-4 space-y-2 flex flex-col justify-start ${mentor === m.id ? 'border-[#2b2240]' : m.flags.length ? 'border-red-100' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-gray-800 flex-1">{m.name}</p>
                      {m.flags.length > 0 ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{m.flags.length} flag{m.flags.length === 1 ? '' : 's'}</span>
                        : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Looks good</span>}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 text-center">
                      {[['Reviews', `${m.reviews}`], ['On time', pct(m.onTimePct)], ['Avg score', m.avgScore === null ? '—' : `${m.avgScore}`], ['Redo', pct(m.redoPct)], ['Words', m.avgWords === null ? '—' : `${m.avgWords}`], ['Helpful', m.helpfulYes + m.helpfulNo ? `${m.helpfulYes}/${m.helpfulYes + m.helpfulNo}` : '—']].map(([k, v]) => (
                        <div key={k} className="bg-gray-50 rounded-lg py-1.5"><p className="text-sm font-bold text-gray-800">{v}</p><p className="text-[9px] font-bold text-gray-400 uppercase">{k}</p></div>
                      ))}
                    </div>
                    {m.waiting > 0 && <p className="text-[11px] text-gray-500">{m.waiting} waiting for review{m.lateNow ? `, ${m.lateNow} late` : ''}</p>}
                    {m.flags.map(f => <p key={f.key} className="text-xs font-semibold text-red-700">⚠️ {f.text}</p>)}
                    {(m.spot.good + m.spot.needs) > 0 && <p className="text-[11px] text-gray-500">Checked: 👍 {m.spot.good} · 👎 {m.spot.needs}</p>}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap pt-2">
              <p className="font-bold text-gray-800 mr-2">Reviews{mentor ? ` by ${data.mentors.find(m => m.id === mentor)?.name}` : ''}</p>
              {([['flagged', 'Worth a look'], ['unchecked', 'Not checked yet'], ['all', 'All']] as [Filter, string][]).map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} aria-pressed={filter === k}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 ${filter === k ? 'border-[#2b2240] bg-[#2b2240] text-white' : 'border-gray-200 text-gray-600 bg-white'}`}>{label}</button>
              ))}
              {mentor && <button onClick={() => setMentor('')} className="text-xs font-semibold text-purple-700 underline">All mentors</button>}
            </div>
            {!rows.length ? <p className="text-sm text-gray-400 py-6 text-center">No reviews here.</p> : (
              <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
                {rows.map(r => (
                  <button key={`${r.taskId}-${r.n}`} onClick={() => setOpen(r)} className="w-full text-left px-4 py-3 hover:bg-purple-50/40 flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <p className="text-sm"><b className="text-gray-800">{r.byName}</b> <span className="text-gray-500">→ {r.studentName}</span> <span className="text-gray-400">· {r.title}</span></p>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">🔧 {r.improve}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.result === 'Accepted' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{r.score}/10 · {r.result}</span>
                      {r.short && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Short</span>}
                      {r.copied && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Copied</span>}
                      {r.late && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Late</span>}
                      {r.helpful === 'no' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">👎 student</span>}
                      {r.checks.length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{r.checks[r.checks.length - 1].verdict === 'good' ? '👍' : '👎'} checked</span>}
                      <span className="text-[11px] text-gray-400">{when(r.at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {open && <SpotCheck auth={auth} row={open} onClose={() => setOpen(null)} onDone={load} />}
    </TrackerLayout>
  );
};

export default ReviewCheck;
